import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, Typography, IconButton, CircularProgress, Alert, Box, Chip, Switch,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { createTenant, linkTenantToUnit, checkTenantByIdNumber, fetchApartments, fetchCategories, fetchUnits } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// Proration preview — mirrors backend logic in tenant_utils.py
// calculate_prorated_amount() and round_up_to_divisible_by_three()
// ─────────────────────────────────────────────────────────────────────────────
const computeProratedPreview = (leaseStart, rent, bills, billInclusions) => {
  if (!leaseStart || !rent) return null;

  const d         = new Date(leaseStart);
  const day       = d.getDate();
  const year      = d.getFullYear();
  const month     = d.getMonth();
  const daysInMth = new Date(year, month + 1, 0).getDate();

  let ratio = 1;
  let adjustedDay = day;

  if (day > 27) {
    ratio = 0;
  } else if (day > 5) {
    adjustedDay = day % 3 === 0 ? day : day + (3 - (day % 3));
    ratio = (daysInMth - adjustedDay) / daysInMth;
  }

  // Round to nearest 10 — matches backend ROUND_TO_TEN behaviour
  const r10 = (n) => Math.round(n / 10) * 10;

  const rentAmt = parseFloat(rent) || 0;
  const proRent = r10(rentAmt * ratio);

  const billLines = bills.map((b, i) => ({
    type:     b.type,
    amount:   parseFloat(b.amount) || 0,
    included: billInclusions[i] !== false && !!b.amount,
    prorated: r10((parseFloat(b.amount) || 0) * ratio),
  }));

  const billsTotal = billLines
    .filter(b => b.included)
    .reduce((s, b) => s + b.prorated, 0);

  return {
    day, daysInMth, adjustedDay,
    ratioPercent: Math.round(ratio * 100),
    isFullMonth:  day <= 5,
    isNextMonth:  day > 27,
    proRent,
    billLines,
    billsTotal,
    total: proRent + billsTotal,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// TenantForm
// ─────────────────────────────────────────────────────────────────────────────
const TenantForm = ({ open, onClose, onTenantAdded }) => {

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    idNumber: '', unitId: '', leaseStart: '', leaseEnd: '', deposit: '',
    utilityBills: [
      { type: 'WiFi',               amount: '' },
      { type: 'Garbage Collection', amount: '' },
    ],
  });

  const [selectedApartmentId, setSelectedApartmentId] = useState('');
  const [selectedCategoryId,  setSelectedCategoryId]  = useState('');
  const [apartments,     setApartments]     = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);

  const [mode,           setMode]           = useState('create');
  const [existingTenant, setExistingTenant] = useState(null);
  const [idCheckLoading, setIdCheckLoading] = useState(false);
  const [idCheckDone,    setIdCheckDone]    = useState(false);
  const idCheckTimer = useRef(null);

  const [useProration,    setUseProration]   = useState(true);
  const [billInclusions,  setBillInclusions] = useState({});
  const [startingBalance, setStartingBalance] = useState('');

  const [newUtilityType,    setNewUtilityType]    = useState('');
  const [newUtilityAmount,  setNewUtilityAmount]  = useState('');
  const [loading,           setLoading]           = useState(false);
  const [apartmentsLoading, setApartmentsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [unitsLoading,      setUnitsLoading]      = useState(false);
  const [error,             setError]             = useState('');

  const selectedUnit = availableUnits.find(u => u.id === formData.unitId);
  const unitRent     = selectedUnit?.rent ?? 0;

  const proratedPreview = useMemo(() => {
    if (!useProration) return null;
    return computeProratedPreview(formData.leaseStart, unitRent, formData.utilityBills, billInclusions);
  }, [formData.leaseStart, unitRent, formData.utilityBills, billInclusions, useProration]);

  const showLowBalanceWarning = (
    !useProration &&
    startingBalance !== '' &&
    parseFloat(startingBalance) < parseFloat(formData.deposit || 0) &&
    parseFloat(formData.deposit || 0) > 0
  );

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => { if (open) fetchApartmentsList(); }, [open]);

  useEffect(() => {
    if (selectedApartmentId) {
      fetchCategoriesList(selectedApartmentId);
      setSelectedCategoryId('');
      setFormData(p => ({ ...p, unitId: '' }));
      setAvailableUnits([]);
    }
  }, [selectedApartmentId]);

  useEffect(() => {
    if (selectedCategoryId) {
      fetchUnitsList(selectedCategoryId);
      setFormData(p => ({ ...p, unitId: '' }));
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    setBillInclusions(prev => {
      const next = {};
      formData.utilityBills.forEach((_, i) => { next[i] = prev[i] !== undefined ? prev[i] : true; });
      return next;
    });
  }, [formData.utilityBills.length]);

  // ── id check ───────────────────────────────────────────────────────────────
  const handleIdNumberChange = (e) => {
    const value = e.target.value;
    setFormData(p => ({ ...p, idNumber: value }));
    setIdCheckDone(false); setExistingTenant(null);
    if (mode === 'link') setMode('create');
    if (idCheckTimer.current) clearTimeout(idCheckTimer.current);
    if (value.trim().length >= 5)
      idCheckTimer.current = setTimeout(() => checkIdNumber(value.trim()), 600);
  };

  const checkIdNumber = async (idNumber) => {
    setIdCheckLoading(true);
    try { const res = await checkTenantByIdNumber(idNumber); setIdCheckDone(true); if (res.exists) setExistingTenant(res); }
    catch { /* ignore */ }
    finally { setIdCheckLoading(false); }
  };

  const switchToLinkMode = () => {
    if (!existingTenant) return;
    setMode('link');
    setFormData(p => ({
      ...p,
      firstName: existingTenant.name?.split(' ')[0] || p.firstName,
      lastName:  existingTenant.name?.split(' ').slice(1).join(' ') || p.lastName,
      phone:  existingTenant.phone  || p.phone,
      email:  existingTenant.email  || p.email,
      idNumber: existingTenant.idNumber || p.idNumber,
      unitId: '', leaseStart: '', leaseEnd: '', deposit: '',
      utilityBills: [{ type: 'WiFi', amount: '' }, { type: 'Garbage Collection', amount: '' }],
    }));
    setSelectedApartmentId(''); setSelectedCategoryId(''); setAvailableUnits([]);
  };

  // ── fetchers ───────────────────────────────────────────────────────────────
  const fetchApartmentsList = async () => {
    try { setApartmentsLoading(true); setApartments(await fetchApartments()); }
    catch { setError('Failed to load properties.'); } finally { setApartmentsLoading(false); }
  };
  const fetchCategoriesList = async (id) => {
    try { setCategoriesLoading(true); setCategories(await fetchCategories(id)); }
    catch { setError('Failed to load categories.'); } finally { setCategoriesLoading(false); }
  };
  const fetchUnitsList = async (id) => {
    try { setUnitsLoading(true); const list = await fetchUnits(id); setAvailableUnits(list.filter(u => !u.occupied)); }
    catch { setError('Failed to load units.'); } finally { setUnitsLoading(false); }
  };

  // ── form handlers ──────────────────────────────────────────────────────────
  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleUtilityChange = (i, field, value) => {
    const updated = [...formData.utilityBills];
    updated[i] = { ...updated[i], [field]: value };
    setFormData(p => ({ ...p, utilityBills: updated }));
  };

  const handleAddUtility = () => {
    if (newUtilityType.trim() && newUtilityAmount) {
      setFormData(p => ({ ...p, utilityBills: [...p.utilityBills, { type: newUtilityType, amount: newUtilityAmount }] }));
      setNewUtilityType(''); setNewUtilityAmount('');
    }
  };

  const handleRemoveUtility = (index) => {
    const updated = [...formData.utilityBills];
    updated.splice(index, 1);
    setFormData(p => ({ ...p, utilityBills: updated }));
    setBillInclusions(prev => {
      const next = {};
      updated.forEach((_, i) => { next[i] = i < index ? prev[i] : prev[i + 1]; });
      return next;
    });
  };

  const toggleBillInclusion = (i) =>
    setBillInclusions(prev => ({ ...prev, [i]: !prev[i] }));

  // ── validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const required = mode === 'link'
      ? ['unitId', 'leaseStart', 'deposit']
      : ['firstName', 'lastName', 'phone', 'idNumber', 'unitId', 'leaseStart', 'deposit'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length > 0) { setError(`Please fill in: ${missing.join(', ')}`); return false; }
    if (!useProration && startingBalance === '') {
      setError('Starting balance is required when proration is off. Enter 0 if no charge this month.');
      return false;
    }
    return true;
  };

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true); setError('');
    try {
      const bills = formData.utilityBills
        .filter(b => b.type.trim() && b.amount)
        .map((b, i) => ({
          type: b.type, amount: parseFloat(b.amount),
          includeInFirstMonth: useProration ? (billInclusions[i] !== false) : true,
        }));

      const basePayload = {
        unitId: formData.unitId, leaseStart: formData.leaseStart,
        leaseEnd: formData.leaseEnd || null, deposit: parseFloat(formData.deposit),
        useProration, utilityBills: bills,
        ...(!useProration && { startingBalance: parseFloat(startingBalance) }),
      };

      const result = mode === 'link'
        ? await linkTenantToUnit(existingTenant.tenantId, basePayload)
        : await createTenant({ firstName: formData.firstName, lastName: formData.lastName, email: formData.email || null, phone: formData.phone, idNumber: formData.idNumber, ...basePayload });

      resetForm();
      if (onTenantAdded) onTenantAdded(result);
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to save tenant. Please try again.');
    } finally { setLoading(false); }
  };

  // ── reset ──────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ firstName: '', lastName: '', email: '', phone: '', idNumber: '', unitId: '', leaseStart: '', leaseEnd: '', deposit: '',
      utilityBills: [{ type: 'WiFi', amount: '' }, { type: 'Garbage Collection', amount: '' }] });
    setSelectedApartmentId(''); setSelectedCategoryId(''); setAvailableUnits([]);
    setMode('create'); setExistingTenant(null); setIdCheckDone(false);
    setUseProration(true); setBillInclusions({}); setStartingBalance(''); setError('');
  };

  const handleClose = () => { resetForm(); onClose(); };
  const isLinkMode = mode === 'link';
  const rentAmount = selectedUnit?.rent ?? null;

  const sectionLabel = {
    fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'text.secondary', mb: 1,
  };
  const panel = { p: 2, borderRadius: 2, border: '1px solid', mb: 1.5 };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '92vh', maxHeight: '92vh', width: '100%', display: 'flex', flexDirection: 'column' } }}>

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {isLinkMode
          ? <><LinkIcon fontSize="small" color="primary" /> Link Tenant to New Unit</>
          : <><PersonAddIcon fontSize="small" color="primary" /> Add New Tenant</>}
        {isLinkMode && <Chip label="Link Mode" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />}
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 2.5, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* existing tenant banner */}
        {idCheckDone && existingTenant && !isLinkMode && (
          <Alert severity="info" sx={{ mb: 2 }}
            action={<Button size="small" color="inherit" startIcon={<LinkIcon />} onClick={switchToLinkMode}>Link Instead</Button>}>
            <strong>{existingTenant.name}</strong> already has a tenancy (unit {existingTenant.currentUnit} · {existingTenant.currentApartment}).
          </Alert>
        )}

        {isLinkMode && existingTenant && (
          <Alert severity="info" sx={{ mb: 2 }}
            action={<Button size="small" color="inherit" onClick={() => { setMode('create'); setExistingTenant(null); setIdCheckDone(false); }}>
              ← New Tenant Instead
            </Button>}>
            Linking <strong>{existingTenant.name}</strong> · Personal details are pre-filled and locked.
          </Alert>
        )}

        <Grid container spacing={3} sx={{ flex: 1, alignItems: 'flex-start', mt: 0 }}>

          {/* ══════════════════════════════════════
              LEFT — personal info + property
          ══════════════════════════════════════ */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>

            <Typography sx={sectionLabel}>
              Personal Information
              {isLinkMode && <Box component="span" sx={{ ml: 1, fontWeight: 400, textTransform: 'none', fontSize: '0.7rem' }}>(read-only)</Box>}
            </Typography>

            <Grid container spacing={1.5}>
              {[
                { name: 'firstName', label: 'First Name',   sm: 6 },
                { name: 'lastName',  label: 'Last Name',    sm: 6 },
                { name: 'email',     label: 'Email',        sm: 12, required: false },
                { name: 'phone',     label: 'Phone Number', sm: 6 },
              ].map(({ name, label, sm, required = true }) => (
                <Grid item xs={12} sm={sm} key={name}>
                  <TextField required={required} fullWidth size="small" name={name} label={label}
                    value={formData[name]} onChange={handleChange} disabled={isLinkMode}
                    sx={isLinkMode ? { '& .MuiInputBase-root': { bgcolor: 'action.hover' } } : {}} />
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth size="small" name="idNumber" label="ID Number"
                  value={formData.idNumber} onChange={isLinkMode ? undefined : handleIdNumberChange}
                  disabled={isLinkMode}
                  InputProps={{
                    readOnly: isLinkMode,
                    endAdornment: idCheckLoading
                      ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                      : null,
                  }}
                  sx={isLinkMode ? { '& .MuiInputBase-root': { bgcolor: 'action.hover' } } : {}} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField required fullWidth size="small" name="deposit" label="Security Deposit" type="number"
                  value={formData.deposit} onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }} />
              </Grid>
            </Grid>

            <Typography sx={{ ...sectionLabel, mt: 2.5 }}>
              {isLinkMode ? 'New Unit' : 'Property Information'}
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Select Property</InputLabel>
                  <Select value={selectedApartmentId} label="Select Property"
                    onChange={e => setSelectedApartmentId(e.target.value)} disabled={apartmentsLoading}>
                    {apartmentsLoading ? <MenuItem disabled>Loading…</MenuItem>
                      : apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required disabled={!selectedApartmentId}>
                  <InputLabel>Category</InputLabel>
                  <Select value={selectedCategoryId} label="Category"
                    onChange={e => setSelectedCategoryId(e.target.value)} disabled={categoriesLoading || !selectedApartmentId}>
                    {categoriesLoading ? <MenuItem disabled>Loading…</MenuItem>
                      : categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required disabled={!selectedCategoryId}>
                  <InputLabel>Unit</InputLabel>
                  <Select name="unitId" value={formData.unitId} label="Unit"
                    onChange={handleChange} disabled={unitsLoading || !selectedCategoryId}>
                    {unitsLoading ? <MenuItem disabled>Loading…</MenuItem>
                      : availableUnits.length === 0 ? <MenuItem disabled>No available units</MenuItem>
                      : availableUnits.map(u => (
                          <MenuItem key={u.id} value={u.id}>
                            Unit {u.number} — KES {Number(u.rent).toLocaleString()}/mo
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </Grid>
              {rentAmount && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={false} sx={{ py: 0.5, fontSize: '0.8rem' }}>
                    Monthly rent: <strong>KES {Number(rentAmount).toLocaleString()}</strong>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField required fullWidth size="small" name="leaseStart" label="Lease Start"
                  type="date" InputLabelProps={{ shrink: true }}
                  value={formData.leaseStart} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" name="leaseEnd" label="Lease End (optional)"
                  type="date" InputLabelProps={{ shrink: true }}
                  value={formData.leaseEnd} onChange={handleChange} />
              </Grid>
            </Grid>
          </Grid>

          {/* ══════════════════════════════════════
              RIGHT — charges + proration
          ══════════════════════════════════════ */}
          <Grid item xs={12} md={6} sx={{
            display: 'flex', flexDirection: 'column', height: '100%',
            borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' },
            pl: { md: 3 },
          }}>

            {/* section header + toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ ...sectionLabel, mb: 0 }}>Monthly Charges</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={useProration
                  ? 'Proration ON — first month charge is calculated proportionally by days remaining in the month'
                  : 'Proration OFF — you will enter the starting balance manually below'}>
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
                <Typography variant="caption" fontWeight={700}
                  sx={{ color: useProration ? 'success.main' : 'text.secondary' }}>
                  {useProration ? 'Proration ON' : 'Proration OFF'}
                </Typography>
                <Switch size="small" checked={useProration} color="success"
                  onChange={e => { setUseProration(e.target.checked); if (e.target.checked) setStartingBalance(''); }} />
              </Box>
            </Box>

            {/* bill rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              {formData.utilityBills.map((bill, i) => {
                const excluded = useProration && billInclusions[i] === false;
                return (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 1.5, borderRadius: 1.5, border: '1px solid',
                    borderColor: excluded ? 'warning.200' : 'divider',
                    bgcolor: excluded ? 'warning.50' : 'background.paper',
                    opacity: excluded ? 0.7 : 1,
                    transition: 'all 0.15s',
                  }}>
                  <TextField size="small" label="Type" value={bill.type} sx={{ flex: 1 }}
                    onChange={e => handleUtilityChange(i, 'type', e.target.value)} />
                  <TextField size="small" label="Amount" type="number" value={bill.amount} sx={{ flex: 1 }}
                    onChange={e => handleUtilityChange(i, 'amount', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }} />

                  {/* per-bill first-month toggle — only when proration ON */}
                  {useProration && (
                    <Tooltip title={excluded
                      ? `"${bill.type || 'This charge'}" will NOT be charged this first month`
                      : `"${bill.type || 'This charge'}" WILL be charged this first month`}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
                        <Switch size="small" checked={billInclusions[i] !== false}
                          onChange={() => toggleBillInclusion(i)} color="primary" />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: excluded ? 'warning.main' : 'text.secondary', lineHeight: 1.2 }}>
                          {excluded ? 'Skip' : 'Inc.'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}

                  <IconButton size="small" color="error" onClick={() => handleRemoveUtility(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                );
              })}
            </Box>

            {/* add new bill row */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField size="small" label="New Bill Type" value={newUtilityType} sx={{ flex: 1 }}
                placeholder="e.g. Security" onChange={e => setNewUtilityType(e.target.value)} />
              <TextField size="small" label="Amount" type="number" value={newUtilityAmount} sx={{ flex: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }}
                onChange={e => setNewUtilityAmount(e.target.value)} />
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddUtility}
                sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>Add</Button>
            </Box>

            {/* ── PRORATION PREVIEW ───────────────────────────────────────── */}
            {useProration && proratedPreview && (
              <Box sx={{ ...panel, borderColor: 'success.300', bgcolor: 'success.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                  <CalculateIcon sx={{ fontSize: 15, color: 'success.main' }} />
                  <Typography variant="caption" fontWeight={700} color="success.dark"
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    First Month Charge Preview
                  </Typography>
                </Box>

                {proratedPreview.isNextMonth ? (
                  <>
                    <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem', mb: 1.5 }}>
                      Lease starts after the 27th — no rent or bills charged this month. First rent charge will be next full month.
                    </Alert>
                    {/* Security deposit is always charged regardless */}
                    {parseFloat(formData.deposit) > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Security Deposit
                            <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'text.disabled' }}>(full amount)</Box>
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            KES {Number(formData.deposit).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'success.300', pt: 0.75, mt: 0.25,
                          display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight={700} color="success.dark">First Month Total</Typography>
                          <Typography variant="body2" fontWeight={700} color="success.dark">
                            KES {Number(formData.deposit).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                      {proratedPreview.isFullMonth
                        ? `Day ${proratedPreview.day} — charged full month (100%)`
                        : `Day ${proratedPreview.day} → rounded to day ${proratedPreview.adjustedDay} · ${proratedPreview.ratioPercent}% of full month (${proratedPreview.daysInMth - proratedPreview.adjustedDay} of ${proratedPreview.daysInMth} days)`}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Rent</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          KES {Number(proratedPreview.proRent).toLocaleString()}
                          {!proratedPreview.isFullMonth && (
                            <Box component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: 'text.disabled' }}>
                              (of KES {Number(unitRent).toLocaleString()})
                            </Box>
                          )}
                        </Typography>
                      </Box>

                      {proratedPreview.billLines.map((b, i) => b.amount > 0 && (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', opacity: b.included ? 1 : 0.45 }}>
                          <Typography variant="body2" color="text.secondary">
                            {b.type}
                            {!b.included && (
                              <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'warning.main' }}>
                                (skipped this month)
                              </Box>
                            )}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color={b.included ? 'text.primary' : 'text.disabled'}>
                            {b.included ? `KES ${Number(b.prorated).toLocaleString()}` : '—'}
                          </Typography>
                        </Box>
                      ))}

                      {/* Security Deposit — always full amount, shown if entered */}
                      {parseFloat(formData.deposit) > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Security Deposit
                            <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'text.disabled' }}>(full amount)</Box>
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            KES {Number(formData.deposit).toLocaleString()}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ borderTop: '1px solid', borderColor: 'success.300', pt: 0.75, mt: 0.25,
                        display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={700} color="success.dark">First Month Total</Typography>
                        <Typography variant="body2" fontWeight={700} color="success.dark">
                          KES {(Number(proratedPreview.total) + (parseFloat(formData.deposit) || 0)).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── MANUAL STARTING BALANCE ─────────────────────────────────── */}
            {!useProration && (
              <Box sx={{ ...panel, borderColor: 'warning.400', bgcolor: 'warning.50' }}>
                <Typography variant="caption" fontWeight={700} color="warning.dark"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', mb: 1 }}>
                  Starting Balance — First Month
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem', lineHeight: 1.6 }}>
                  Enter the exact amount to charge this tenant for their first month. This replaces
                  proration entirely — monthly charges above are saved for future months only.
                  Enter <strong>0</strong> to charge nothing this month.
                </Typography>

                <TextField required fullWidth size="small" label="Starting Balance" type="number"
                  value={startingBalance} onChange={e => setStartingBalance(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }}
                  helperText="Cannot be left blank. Enter 0 for no first-month charge." />

                {showLowBalanceWarning && (
                  <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />}
                    sx={{ mt: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                    Starting balance (KES {Number(startingBalance).toLocaleString()}) is less than the
                    security deposit (KES {Number(formData.deposit).toLocaleString()}).
                    This amount is what will be imposed on the tenant's account as their first-month charge —
                    it does not include the security deposit, which is charged separately.
                  </Alert>
                )}
              </Box>
            )}

            {/* spacer — pushes content up, fills empty space below */}
            <Box sx={{ flex: 1 }} />

          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : isLinkMode ? <LinkIcon /> : <PersonAddIcon />}>
          {loading
            ? (isLinkMode ? 'Linking…' : 'Adding…')
            : (isLinkMode ? 'Link to Unit' : 'Add Tenant')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantForm;